import { useState, useEffect, useRef } from "react";
import "./ImageViewerModal.css";
import apiClient from "../../api/apiClient";

// uploadEndpoint: optional override for the upload URL.
// Defaults to /coas/upload-picture/:coaId for backward compat.
// After product table migration, pass `/products/upload-picture/:productId`.
function ImageViewerModal({ coaId, pictures, onClose, onUpload, canUpload = false, uploadEndpoint }) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === "Escape") onClose();
        }; 
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onClose]);

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append("file", file);
                const endpoint = uploadEndpoint || `/coas/upload-picture/${coaId}`;
                await apiClient.post(endpoint, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }
            await onUpload();
        } catch (err) {
            console.error("Upload failed:", err);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = null;
        }
    };

    const currentPicture = pictures[selectedIndex] || null;

    return (
        <div className="img-modal-overlay" onClick={onClose}>
            <div className="img-modal-panel" onClick={(e) => e.stopPropagation()}>
                <div className="img-modal-header">
                    <h3>Images{pictures.length > 0 ? ` (${pictures.length})` : ""}</h3>
                    <button className="img-modal-close" onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </div>

                <div className="img-modal-body">
                    <div className="img-modal-sidebar">
                        {pictures.map((pic, i) => (
                            <div
                                key={pic.id}
                                className={`img-modal-thumb-item${selectedIndex === i ? " active" : ""}`}
                                onClick={() => setSelectedIndex(i)}
                            >
                                <img src={pic.picture_url} alt={`Image ${i + 1}`} className="img-modal-thumb" />
                                <a
                                    href={pic.picture_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="img-modal-new-tab"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Open in new tab"
                                >
                                    ↗
                                </a>
                            </div>
                        ))}
                        {canUpload && (
                            <div className="img-modal-upload-area">
                                <button
                                    className="img-modal-upload-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                >
                                    {uploading ? "Uploading…" : "Upload Image"}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style={{ display: "none" }}
                                    onChange={handleFileChange}
                                />
                            </div>
                        )}
                    </div>

                    <div className="img-modal-viewer">
                        {currentPicture ? (
                            <img
                                src={currentPicture.picture_url}
                                alt={`Image ${selectedIndex + 1}`}
                                className="img-modal-full"
                            />
                        ) : (
                            <span className="img-modal-no-image">No images yet. Upload one using the button.</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImageViewerModal;
